import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router-dom";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm"; // GFM tables — without this, pipe tables render as raw text
import { markdownComponents } from "@/ui/CommunicationView/ChatConversation/PromptMessageContainer/markdown";
import { AppShellV8 } from "../AppShellV8";
import Button from "@/ui/Button";
import { ButtonTypes } from "@/ui/types";
import { DOCS_API_DOMAIN } from "@launchos/shared-utils/env";
import { getActiveClient, useActiveClient, getCurrentMemberId } from "../activeClient";
import { EntityPicker } from "../EntityPicker";

// AMB-420: the campaign LIST now scopes to the live top-bar client (see useActiveClient in
// the Campaigns component). Everything else here reads/writes a deliverable or campaign BY ID,
// which the operator API resolves across all clients under the agency "all" scope (same as the
// TaskDrawer) — so these by-id calls use "all" and never get stranded on a stale client slug.
const CAMP_CLIENT_SLUG = "all";

/* ───── Types ───── */
type Status = "secured" | "drafted" | "pending-review" | "to-draft" | "blocked";
type ViewMode = "assets" | "schedule" | "rationale";

interface Asset {
  id: string;
  name: string;
  type: string;
  status: Status;
  agent: string;
  due: string;
  rationale?: string;
  meetingSource?: string;
  /** Optional external content URL (e.g. a Google Doc) when content lives elsewhere. */
  sourceUrl?: string;
  /** Frontmatter phase id this asset belongs to (undefined = unphased). */
  phase?: string;
  /** When set, the deliverable has been flagged for review. */
  reviewState?: "in-review";
}

/** Seed shape for creating a new deliverable (from NewDeliverableModal). */
interface SeedAsset {
  name: string;
  type: string;
  status: Status;
  agent: string;
  due: string;
}

interface Phase {
  id: number;
  slug?: string; // frontmatter phase id (FK) — undefined for the unphased bucket
  name: string;
  dates: string;
  colorBar: string;
  colorBg: string;
  secured: number;
  total: number;
  assets: Asset[];
}

interface Campaign {
  id: string;
  name: string;
  client: string;
  promise: string;
  status: "active" | "draft" | "complete" | "planned";
  dates: string;
  progress: number;
  /** Optional explicit asset counts for the progress-ring label. Falls back to phase totals. */
  assetsSecured?: number;
  assetsTotal?: number;
  /** Next thing that needs work — shown on the card. */
  nextMilestone?: { label: string; date: string };
  phases: Phase[];
  rawPhases: PhaseDef[];
  foundation: string[];
  startDate: string;
  endDate: string;
  channels: string[];
}

// Editable phase definition (mirrors the campaign frontmatter `phases[]`).
interface PhaseDef {
  id: string;
  name: string;
  dates?: { start?: string; end?: string };
}

/* ───── Constants ───── */
const STATUS_CHIP: Record<Status, { label: string; classes: string }> = {
  secured:          { label: "Secured",        classes: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60" },
  drafted:          { label: "Drafted",        classes: "bg-blue-50 text-blue-700 ring-1 ring-blue-200/60" },
  "pending-review": { label: "Pending Review", classes: "bg-amber-50 text-amber-700 ring-1 ring-amber-200/60" },
  "to-draft":       { label: "To Draft",       classes: "bg-gray-50 text-gray-500 ring-1 ring-gray-200/60" },
  blocked:          { label: "Blocked",        classes: "bg-red-50 text-red-700 ring-1 ring-red-200/60" },
};

const TYPE_BADGE: Record<string, string> = {
  Email:  "bg-sky-50 text-sky-700 ring-1 ring-sky-200/50",
  Page:   "bg-purple-50 text-purple-700 ring-1 ring-purple-200/50",
  Social: "bg-pink-50 text-pink-700 ring-1 ring-pink-200/50",
  Ad:     "bg-amber-50 text-amber-700 ring-1 ring-amber-200/50",
  Doc:    "bg-gray-50 text-gray-600 ring-1 ring-gray-200/50",
  Config: "bg-teal-50 text-teal-700 ring-1 ring-teal-200/50",
  Data:   "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200/50",
  Script: "bg-orange-50 text-orange-700 ring-1 ring-orange-200/50",
  Assets: "bg-lime-50 text-lime-700 ring-1 ring-lime-200/50",
};

const CAMPAIGN_STATUS: Record<string, { label: string; classes: string }> = {
  active:   { label: "Active",   classes: "bg-blue-50 text-blue-700 ring-1 ring-blue-200/60" },
  draft:    { label: "Draft",    classes: "bg-gray-50 text-gray-500 ring-1 ring-gray-200/60" },
  complete: { label: "Complete", classes: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60" },
  planned:  { label: "Planned",  classes: "bg-violet-50 text-violet-700 ring-1 ring-violet-200/60" },
};

/* ───── Real-data projection (operator API → Campaign) ───── */
const CAMP_CLIENT_NAME: Record<string, string> = { [getActiveClient().slug]: getActiveClient().name };
// Humanize an ISO date (2026-05-01) → "May 1, 2026"; pass through anything unparseable.
const fmtCampaignDate = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
// Strip markdown syntax from a preview snippet so the Foundation cards read as clean
// prose (the full doc renders markdown in the KB) rather than dumping raw `##`/`**`.
const stripMd = (s: string): string =>
  (s || "")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/[*_`>]/g, "")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
const DTYPE_TO_BADGE: Record<string, string> = {
  email: "Email", page: "Page", social: "Social", ad: "Ad", doc: "Doc",
  config: "Config", data: "Data", script: "Script", report: "Doc",
};
const PERSON_SLUG_NAME: Record<string, string> = {
  kyle: "Kyle", claire: "Claire", hannah: "Hannah", ryan: "Ryan", rachel: "Rachel",
};
// Reverse maps for persisting edits back to raw frontmatter values.
const BADGE_TO_DTYPE: Record<string, string> = {
  Email: "email", Page: "page", Social: "social", Ad: "ad", Doc: "doc",
  Config: "config", Data: "data", Script: "script", Assets: "doc",
};
const NAME_TO_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(PERSON_SLUG_NAME).map(([slug, name]) => [name, slug])
);
// Map the screen's combined Status back to frontmatter fields.
const statusToFields = (s: Status): Record<string, unknown> =>
  s === "pending-review" ? { reviewState: "in-review" } : { status: s };
interface ApiDeliverable {
  id: string; title: string; deliverableType?: string; campaign?: string | null;
  phase?: string | null; owner?: string | null; createdBy?: string | null;
  due?: string | null; status?: string | null; reviewState?: string | null; sourceUrl?: string | null;
}
interface ApiCampaign {
  id: string; name: string; client?: string; status?: string | null; promise?: string | null;
  startDate?: string | null; endDate?: string | null; channels?: string[];
  phases?: PhaseDef[]; foundation?: string[];
}
const delToStatus = (d: ApiDeliverable): Status => {
  if (d.status === "blocked") return "blocked";
  if (d.reviewState === "in-review" || d.reviewState === "changes-requested") return "pending-review";
  if (d.status === "secured" || d.status === "deployed") return "secured";
  if (d.status === "to-draft") return "to-draft";
  return "drafted";
};
const apiToAsset = (d: ApiDeliverable): Asset => ({
  id: d.id,
  name: d.title,
  type: DTYPE_TO_BADGE[d.deliverableType || ""] || "Doc",
  status: delToStatus(d),
  agent: PERSON_SLUG_NAME[d.owner || ""] || d.createdBy || "Agent",
  due: d.due || "",
  phase: d.phase || undefined,
  sourceUrl: d.sourceUrl || undefined,
  reviewState: d.reviewState === "in-review" ? "in-review" : undefined,
});
const PHASE_BARS = ["bg-blue-500", "bg-violet-500", "bg-emerald-500", "bg-amber-500"];
function buildPhases(campaignPhases: ApiCampaign["phases"], dels: ApiDeliverable[]): Phase[] {
  const out: Phase[] = [];
  const used = new Set<string>();
  const defined = campaignPhases || [];
  let idx = 0;
  // Render EVERY defined phase — including empty ones — so planning a phase has a
  // visible effect and you can drop assets into it. (Previously empty phases were
  // skipped, which made "Plan phases" look like it did nothing.)
  for (const ph of defined) {
    const assets = dels.filter((d) => d.phase === ph.id).map(apiToAsset);
    assets.forEach((a) => used.add(a.id));
    out.push({ id: idx + 1, slug: ph.id, name: ph.name, dates: "", colorBar: PHASE_BARS[idx % 4], colorBg: "bg-gray-50", secured: assets.filter((a) => a.status === "secured").length, total: assets.length, assets });
    idx++;
  }
  const rest = dels.filter((d) => !used.has(d.id)).map(apiToAsset);
  if (rest.length || !defined.length) {
    // Leftover/unmatched assets → "Unphased" when phases exist, else the single "Assets" bucket.
    out.push({ id: idx + 1, name: defined.length ? "Unphased" : "Assets", dates: "", colorBar: PHASE_BARS[idx % 4], colorBg: "bg-gray-50", secured: rest.filter((a) => a.status === "secured").length, total: rest.length, assets: rest });
  }
  return out;
}
const apiToCampaign = (c: ApiCampaign, allDels: ApiDeliverable[]): Campaign => {
  const dels = allDels.filter((d) => d.campaign === c.id);
  const total = dels.length;
  const secured = dels.filter((d) => d.status === "secured" || d.status === "deployed").length;
  // Channels are DERIVED from the campaign's actual asset types (unique deliverableTypes),
  // not a hand-maintained string. Falls back to any authored channels only when empty.
  const derivedChannels = Array.from(
    new Set(dels.map((d) => DTYPE_TO_BADGE[d.deliverableType || ""] || "").filter(Boolean))
  );
  return {
    id: c.id,
    name: c.name,
    client: CAMP_CLIENT_NAME[c.client || ""] || c.client || "",
    promise: c.promise || "",
    status: (["active", "draft", "complete", "planned"].includes(c.status || "") ? c.status : "active") as Campaign["status"],
    dates: c.startDate && c.endDate ? `${fmtCampaignDate(c.startDate)} – ${fmtCampaignDate(c.endDate)}` : "",
    progress: total ? Math.round((secured / total) * 100) : 0,
    assetsSecured: secured,
    assetsTotal: total,
    phases: buildPhases(c.phases, dels),
    rawPhases: c.phases || [],
    foundation: Array.isArray(c.foundation) ? c.foundation : [],
    startDate: c.startDate || "",
    endDate: c.endDate || "",
    channels: derivedChannels.length ? derivedChannels : (c.channels || []),
  };
};

/* ───── Campaign Data ───── */
let __assetSeq = 0;
const newAssetId = () => `a${++__assetSeq}`;

/* The Related panel (Foundation docs, Pending Reviews, Recent Meetings) is now
   fetched live inside CampaignDetail — see relFoundation/relReviews/relMeetings. */

/* ───── Shared CRUD primitives ───── */

/** Three-dots icon button + portal-positioned popover menu with click-outside backdrop. */
interface MenuItem {
  label: string;
  onClick: () => void;
  destructive?: boolean;
  icon?: React.ReactNode;
}
function OverflowMenu({
  items,
  alwaysVisible = false,
  buttonClassName = "",
  stopPropagation = true,
  label,
}: {
  items: MenuItem[];
  /** detail headers: always visible. cards/rows: hover-revealed. */
  alwaysVisible?: boolean;
  buttonClassName?: string;
  stopPropagation?: boolean;
  /** When set, render a labeled button (text + dots) instead of a bare ⋯ icon. */
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);

  const openMenu = (e: React.MouseEvent) => {
    if (stopPropagation) e.stopPropagation();
    e.preventDefault();
    // Position off the clicked element (works whether the trigger is the bare ⋯
    // button or the platform Button used for the labeled top-bar variant).
    const r = (e.currentTarget as HTMLElement)?.getBoundingClientRect() || btnRef.current?.getBoundingClientRect();
    if (r) {
      // Right-align the menu to the button; clamp to viewport.
      const left = Math.max(8, Math.min(r.right - 160, window.innerWidth - 168));
      setCoords({ top: r.bottom + 4, left });
    }
    setOpen(true);
  };

  return (
    <>
      {label ? (
        // Labeled variant (e.g. top-bar "Campaign actions") — render as the platform
        // Button so it matches the other top-bar actions exactly. A trailing chevron
        // makes it read as a dropdown menu rather than a static label/button (UX-C8).
        <Button
          label={
            <span className="inline-flex items-center gap-1.5">
              {label}
              <svg className="h-3.5 w-3.5 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            </span>
          }
          type={ButtonTypes.OUTLINED}
          onClick={openMenu}
          className={buttonClassName}
        />
      ) : (
        <button
          ref={btnRef}
          type="button"
          onClick={openMenu}
          aria-label="More actions"
          className={`rounded p-1 text-gray-400 transition-all duration-150 hover:bg-gray-100 ${
            alwaysVisible ? "" : "opacity-0 group-hover:opacity-100"
          } ${buttonClassName}`}
        >
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" />
          </svg>
        </button>
      )}
      {open &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[60]" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
            <div
              className="fixed z-[61] min-w-[160px] rounded-lg bg-white py-1 text-[13px] shadow-xl ring-1 ring-black/[0.06]"
              style={{ top: coords.top, left: coords.left }}
              onClick={(e) => e.stopPropagation()}
            >
              {items.map((it, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => { setOpen(false); it.onClick(); }}
                  className={`flex w-full items-center gap-2 px-3 py-1.5 text-left transition-all duration-150 hover:bg-gray-50 ${
                    it.destructive ? "text-red-600 hover:bg-red-50" : "text-gray-700"
                  }`}
                >
                  {it.icon && <span className="shrink-0">{it.icon}</span>}
                  {it.label}
                </button>
              ))}
            </div>
          </>,
          document.body
        )}
    </>
  );
}

/** Centered modal shell with backdrop + click-outside-to-close. */
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <>
      <div className="fixed inset-0 z-[70] bg-black/20 backdrop-blur-[2px] transition-opacity duration-150" onClick={onClose} />
      <div className="fixed inset-0 z-[71] flex items-center justify-center p-4" onClick={onClose}>
        <div
          className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-black/[0.06]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-[15px] font-bold tracking-tight text-gray-900">{title}</h2>
            <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {children}
        </div>
      </div>
    </>,
    document.body
  );
}

/* Shared form field styling */
const FIELD = "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-[13px] text-gray-800 outline-none transition-all duration-150 focus:border-gray-400 focus:ring-2 focus:ring-gray-900/[0.06]";
const LABEL = "mb-1 block text-[11px] font-semibold uppercase tracking-wider text-gray-400";
const PRIMARY_BTN = "rounded-lg bg-gray-900 px-4 py-2 text-xs font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.97]";
const GHOST_BTN = "rounded-lg border border-black/[0.06] px-4 py-2 text-xs font-medium text-gray-500 transition-all duration-150 hover:bg-gray-50 hover:text-gray-700";

/** Bottom toast: "Archived · Undo". */
function Toast({ message, onUndo, onDismiss }: { message: string; onUndo: () => void; onDismiss: () => void }) {
  return createPortal(
    <div className="fixed bottom-6 left-1/2 z-[80] -translate-x-1/2">
      <div className="flex items-center gap-3 rounded-xl bg-gray-900 px-4 py-2.5 text-[13px] text-white shadow-2xl ring-1 ring-white/10">
        <span>{message}</span>
        <span className="text-gray-500">·</span>
        <button onClick={onUndo} className="font-semibold text-white underline-offset-2 transition-all duration-150 hover:underline active:scale-[0.97]">Undo</button>
        <button onClick={onDismiss} className="ml-1 rounded p-0.5 text-gray-400 transition-all duration-150 hover:text-white">
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>,
    document.body
  );
}

/* ───── Campaign Edit modal ───── */
function CampaignEditModal({ campaign, onClose, onSave }: { campaign: Campaign; onClose: () => void; onSave: (patch: Partial<Campaign>) => void }) {
  const [name, setName] = useState(campaign.name);
  const [promise, setPromise] = useState(campaign.promise);
  const [status, setStatus] = useState<Campaign["status"]>(campaign.status);
  const [start, setStart] = useState(campaign.startDate || "");
  const [end, setEnd] = useState(campaign.endDate || "");

  const save = () => {
    // Dates persist as { start, end } (parseable) via a direct write; other fields via onSave.
    if (start || end) {
      fetch(`${DOCS_API_DOMAIN}/api/agents/entity/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client: CAMP_CLIENT_SLUG, type: "campaign", id: campaign.id, fields: { dates: { start, end } } }),
      }).catch(() => {});
    }
    onSave({
      name: name.trim() || campaign.name,
      promise: promise.trim(),
      status,
      startDate: start,
      endDate: end,
      dates: start && end ? `${fmtCampaignDate(start)} – ${fmtCampaignDate(end)}` : "",
    });
    onClose();
  };

  return (
    <Modal title="Edit campaign" onClose={onClose}>
      <div className="space-y-3.5">
        <div>
          <label className={LABEL}>Name</label>
          <input className={FIELD} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className={LABEL}>Promise</label>
          <textarea className={`${FIELD} resize-none`} rows={2} value={promise} onChange={(e) => setPromise(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL}>Status</label>
            <select className={FIELD} value={status} onChange={(e) => setStatus(e.target.value as Campaign["status"])}>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="planned">Planned</option>
              <option value="complete">Complete</option>
            </select>
          </div>
          <div>
            <label className={LABEL}>Start</label>
            <input type="date" className={FIELD} value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div />
          <div>
            <label className={LABEL}>End</label>
            <input type="date" className={FIELD} value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
        </div>
        <p className="text-[11px] text-gray-400">Channels are derived automatically from the campaign's asset types.</p>
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <button className={GHOST_BTN} onClick={onClose}>Cancel</button>
        <button className={PRIMARY_BTN} onClick={save}>Save</button>
      </div>
    </Modal>
  );
}

/* ───── Deliverable / Asset modals ───── */
const DELIVERABLE_TYPES = ["Email", "Page", "Social", "Ad", "Doc", "Config", "Data", "Script", "Assets"];

/** Manage a campaign's phases — add / rename / remove / reorder + dates. */
function ManagePhasesModal({ initial, counts = {}, onClose, onSave }: { initial: PhaseDef[]; counts?: Record<string, number>; onClose: () => void; onSave: (phases: PhaseDef[]) => void }) {
  const [rows, setRows] = useState<PhaseDef[]>(initial.map((p) => ({ id: p.id, name: p.name, dates: { ...(p.dates || {}) } })));
  const update = (i: number, patch: Partial<PhaseDef>) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const setDate = (i: number, key: "start" | "end", v: string) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, dates: { ...r.dates, [key]: v } } : r)));
  const move = (i: number, dir: number) => setRows((rs) => { const n = [...rs]; const j = i + dir; if (j < 0 || j >= n.length) return rs; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const remove = (i: number) => setRows((rs) => rs.filter((_, idx) => idx !== i));
  const add = () => setRows((rs) => [...rs, { id: "", name: "", dates: {} }]);
  const save = () => {
    onSave(
      rows.filter((r) => r.name.trim()).map((r) => {
        const slug = r.id || r.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
        const hasDates = r.dates?.start || r.dates?.end;
        return { id: slug, name: r.name.trim(), ...(hasDates ? { dates: r.dates } : {}) };
      })
    );
    onClose();
  };
  return (
    <Modal title="Manage phases" onClose={onClose}>
      <p className="mb-3.5 text-xs leading-relaxed text-gray-500">
        Phases break this campaign into stages (e.g. Pre-launch → Launch → Follow-up). Reorder with the arrows and set optional dates.
        Assets land in a phase via each asset&rsquo;s <span className="font-medium text-gray-600">Move to phase</span> menu, or <span className="font-medium text-gray-600">+ New deliverable</span> inside a phase.
      </p>
      <div className="space-y-2.5">
        {rows.length === 0 && <p className="rounded-lg border border-dashed border-black/[0.08] py-4 text-center text-xs text-gray-400">No phases yet — add your first stage below.</p>}
        {rows.map((r, i) => {
          const count = r.id ? (counts[r.id] || 0) : 0;
          return (
            <div key={i} className="rounded-xl border border-black/[0.06] bg-white p-3 shadow-sm ring-1 ring-black/[0.03]">
              <div className="flex items-center gap-2.5">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-bold text-gray-500">{i + 1}</span>
                <input value={r.name} onChange={(e) => update(i, { name: e.target.value })} placeholder="Phase name (e.g. Launch)" className={`${FIELD} min-w-0 flex-1`} />
                {r.id && <span className="shrink-0 rounded-full bg-gray-50 px-2 py-0.5 text-[10px] font-medium text-gray-400 ring-1 ring-black/[0.04]">{count} asset{count !== 1 ? "s" : ""}</span>}
                <div className="flex flex-col leading-none">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="px-1 text-[10px] text-gray-400 transition-all hover:text-gray-700 disabled:opacity-25" aria-label="Move up">▲</button>
                  <button onClick={() => move(i, 1)} disabled={i === rows.length - 1} className="px-1 text-[10px] text-gray-400 transition-all hover:text-gray-700 disabled:opacity-25" aria-label="Move down">▼</button>
                </div>
                <button onClick={() => remove(i)} className="rounded-md p-1.5 text-gray-400 transition-all hover:bg-red-50 hover:text-red-600" aria-label="Remove phase">✕</button>
              </div>
              {/* AMB-401: native date inputs have an intrinsic min-width and won't shrink
                  inside the grid — min-w-0 on the cells + inputs lets them fit so the row
                  never bleeds past the modal edge. */}
              <div className="mt-2.5 grid grid-cols-2 gap-2 pl-[34px]">
                <label className="block min-w-0"><span className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-gray-400">Start</span><input type="date" value={r.dates?.start || ""} onChange={(e) => setDate(i, "start", e.target.value)} className={`${FIELD} min-w-0`} /></label>
                <label className="block min-w-0"><span className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-gray-400">End</span><input type="date" value={r.dates?.end || ""} onChange={(e) => setDate(i, "end", e.target.value)} className={`${FIELD} min-w-0`} /></label>
              </div>
            </div>
          );
        })}
        <button onClick={add} className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-black/[0.12] py-2.5 text-[13px] font-medium text-gray-500 transition-all hover:border-blue-300 hover:bg-blue-50/40 hover:text-blue-600">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
          Add phase
        </button>
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <button className={GHOST_BTN} onClick={onClose}>Cancel</button>
        <button className={PRIMARY_BTN} onClick={save}>Save phases</button>
      </div>
    </Modal>
  );
}

/** New deliverable (manual add). */
function NewDeliverableModal({ onClose, onCreate, onLinkExisting }: { onClose: () => void; onCreate: (a: SeedAsset) => void; onLinkExisting?: () => void }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("Email");
  const [agent, setAgent] = useState("Copilot");
  const [due, setDue] = useState("");

  const create = () => {
    if (!name.trim()) return;
    onCreate({ name: name.trim(), type, status: "drafted", agent: agent.trim() || "Copilot", due: due.trim() });
    onClose();
  };

  return (
    <Modal title="New deliverable" onClose={onClose}>
      <div className="space-y-3.5">
        <p className="text-xs text-gray-500">Create a new document for this phase, or <button type="button" onClick={() => onLinkExisting?.()} className="font-medium text-blue-600 hover:underline">link an existing document from the Knowledge Base</button>.</p>
        <div>
          <label className={LABEL}>Name</label>
          <input autoFocus className={FIELD} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Welcome Email #4" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL}>Type</label>
            <select className={FIELD} value={type} onChange={(e) => setType(e.target.value)}>
              {DELIVERABLE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL}>Owner</label>
            <input className={FIELD} value={agent} onChange={(e) => setAgent(e.target.value)} />
          </div>
        </div>
        <div>
          <label className={LABEL}>Due</label>
          <input type="date" className={FIELD} value={due} onChange={(e) => setDue(e.target.value)} />
        </div>
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <button className={GHOST_BTN} onClick={onClose}>Cancel</button>
        <button className={PRIMARY_BTN} onClick={create}>Add deliverable</button>
      </div>
    </Modal>
  );
}

/** Edit existing deliverable. */
function DeliverableEditModal({ asset, onClose, onSave }: { asset: Asset; onClose: () => void; onSave: (patch: Partial<Asset>) => void }) {
  const [name, setName] = useState(asset.name);
  const [type, setType] = useState(asset.type);
  const [status, setStatus] = useState<Status>(asset.status);
  const [agent, setAgent] = useState(asset.agent);
  const [due, setDue] = useState(asset.due);
  const [sourceUrl, setSourceUrl] = useState(asset.sourceUrl || "");

  const save = () => {
    onSave({ name: name.trim() || asset.name, type, status, agent: agent.trim(), due: due.trim(), sourceUrl: sourceUrl.trim() });
    onClose();
  };

  return (
    <Modal title="Edit deliverable" onClose={onClose}>
      <div className="space-y-3.5">
        <div>
          <label className={LABEL}>Name</label>
          <input className={FIELD} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL}>Type</label>
            <select className={FIELD} value={type} onChange={(e) => setType(e.target.value)}>
              {DELIVERABLE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL}>Status</label>
            <select className={FIELD} value={status} onChange={(e) => setStatus(e.target.value as Status)}>
              {(Object.keys(STATUS_CHIP) as Status[]).map((s) => <option key={s} value={s}>{STATUS_CHIP[s].label}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL}>Owner</label>
            <input className={FIELD} value={agent} onChange={(e) => setAgent(e.target.value)} />
          </div>
          <div>
            <label className={LABEL}>Due</label>
            <input type="date" className={FIELD} value={due} onChange={(e) => setDue(e.target.value)} />
          </div>
        </div>
        <div>
          <label className={LABEL}>External content URL <span className="font-normal text-gray-400">(optional — e.g. a Google Doc)</span></label>
          <input className={FIELD} value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="https://…" />
        </div>
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <button className={GHOST_BTN} onClick={onClose}>Cancel</button>
        <button className={PRIMARY_BTN} onClick={save}>Save</button>
      </div>
    </Modal>
  );
}

/** Move an asset to a different phase (or unphased). Radio list of the campaign's phases. */
function MovePhaseModal({ asset, phases, onClose, onMove }: { asset: Asset; phases: PhaseDef[]; onClose: () => void; onMove: (phaseId: string) => void }) {
  const current = asset.phase || "";
  return (
    <Modal title={`Move "${asset.name}" to phase`} onClose={onClose}>
      <div className="space-y-1.5">
        {[{ id: "", name: "Unphased" }, ...phases].map((p) => {
          const isCurrent = (p.id || "") === current;
          return (
            <button
              key={p.id || "__unphased"}
              data-testid={`move-phase-${p.id || "unphased"}`}
              onClick={() => { onMove(p.id); onClose(); }}
              disabled={isCurrent}
              className={`flex w-full items-center justify-between rounded-lg border border-black/[0.06] px-3 py-2.5 text-left transition-all duration-150 ${
                isCurrent ? "cursor-default bg-gray-50 opacity-60" : "hover:border-black/10 hover:bg-gray-50"
              }`}
            >
              <span className="text-[13px] font-medium text-gray-900">{p.name}</span>
              {isCurrent && <span className="text-[10px] font-medium text-gray-400">Current</span>}
            </button>
          );
        })}
        {phases.length === 0 && <p className="px-1 pt-1 text-[11px] text-gray-400">This campaign has no phases yet — add some from “Manage phases.”</p>}
      </div>
      <div className="mt-6 flex justify-end">
        <button className={GHOST_BTN} onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}

/* ───── Progress Circle ───── */
function ProgressCircle({ percent, size = 80, strokeWidth = 6 }: { percent: number; size?: number; strokeWidth?: number }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#f3f4f6" strokeWidth={strokeWidth} />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke="#3b82f6" strokeWidth={strokeWidth} strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-lg font-bold text-gray-900">{percent}%</span>
      </div>
    </div>
  );
}

/* ───── Status badge (differentiated by shape + icon, not hue alone) ───── */
function StatusBadge({ status }: { status: Campaign["status"] }) {
  if (status === "complete") {
    // Neutral/gray badge with a checkmark — distinct from Active green.
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600 ring-1 ring-black/[0.06]">
        <svg className="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
        Complete
      </span>
    );
  }
  if (status === "draft") {
    // Dashed gray badge.
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-2 py-0.5 text-[10px] font-medium text-gray-500">
        <span className="h-1.5 w-1.5 rounded-full border border-gray-400" />
        Draft
      </span>
    );
  }
  if (status === "planned") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700 ring-1 ring-violet-200/60">
        <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
        Planned
      </span>
    );
  }
  // Active — green filled dot/badge.
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-emerald-200/60">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      Active
    </span>
  );
}

/* ───── Card progress indicator: ring (in-flight) or check (complete) ───── */
function CardProgress({ campaign }: { campaign: Campaign }) {
  if (campaign.status === "complete") {
    return (
      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-emerald-50 ring-1 ring-emerald-200/60">
        <svg className="h-6 w-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      </div>
    );
  }
  return <ProgressCircle percent={campaign.progress} size={56} strokeWidth={5} />;
}

/* ───── Single campaign card ───── */
function CampaignCard({
  c,
  onSelect,
  onEdit,
  onDuplicate,
  onArchive,
}: {
  c: Campaign;
  onSelect: (id: string) => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onArchive: () => void;
}) {
  const secured = c.assetsSecured ?? c.phases.reduce((s, p) => s + p.secured, 0);
  const total = c.assetsTotal ?? c.phases.reduce((s, p) => s + p.total, 0);
  const isComplete = c.status === "complete";

  // Progress-ring label — "what 38% means."
  const progressLabel = isComplete
    ? `All ${total} assets delivered`
    : total > 0
      ? `${c.progress}% built · ${secured}/${total} assets`
      : `${c.progress}% built`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(c.id)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(c.id); } }}
      className="group flex cursor-pointer flex-col rounded-xl border border-black/[0.06] bg-white p-5 text-left shadow-sm ring-1 ring-black/[0.04] transition-all duration-150 hover:-translate-y-0.5 hover:border-black/10 hover:shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
    >
      <div className="flex items-start gap-4">
        <CardProgress campaign={c} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-[13px] font-semibold text-gray-900">{c.name}</h3>
            <StatusBadge status={c.status} />
            <div className="ml-auto shrink-0">
              <OverflowMenu
                items={[
                  { label: "Edit", onClick: onEdit },
                  { label: "Duplicate", onClick: onDuplicate },
                  { label: "Archive", onClick: onArchive, destructive: true },
                ]}
              />
            </div>
          </div>
          <p className="mt-0.5 text-xs text-gray-400">{c.client} &middot; {c.dates}</p>
          <p className="mt-1.5 text-[13px] leading-snug text-gray-500 line-clamp-1">{c.promise}</p>
          {/* Progress-ring label */}
          <p className="mt-1.5 text-[11px] font-medium text-gray-400">{progressLabel}</p>
        </div>
      </div>

      {/* Next milestone — "what needs work next" */}
      {c.nextMilestone && (
        <div className={`mt-3.5 flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] ${isComplete ? "bg-gray-50 text-gray-400 ring-1 ring-black/[0.04]" : "bg-amber-50/60 text-amber-700 ring-1 ring-amber-200/40"}`}>
          {isComplete ? (
            <svg className="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v1.5M3 21v-6m0 0l2.77-.693a9 9 0 016.208.682l.108.054a9 9 0 006.086.71l3.114-.732a48.524 48.524 0 01-.005-10.499l-3.11.732a9 9 0 01-6.085-.711l-.108-.054a9 9 0 00-6.208-.682L3 4.5M3 15V4.5" />
            </svg>
          )}
          <span className="font-semibold">{isComplete ? "Next:" : "Next:"}</span>
          <span className="truncate">{c.nextMilestone.label}</span>
          <span className="shrink-0 opacity-70">&middot; {c.nextMilestone.date}</span>
        </div>
      )}

      {/* Channels + Open affordance */}
      <div className="mt-3.5 flex items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          {c.channels.map((ch) => (
            <span key={ch} className="rounded-md bg-gray-50 px-2 py-0.5 text-[10px] font-medium text-gray-500 ring-1 ring-black/[0.04]">{ch}</span>
          ))}
        </div>
        <span className="flex shrink-0 items-center gap-0.5 text-[11px] font-medium text-gray-300 transition-all duration-150 group-hover:text-gray-600">
          Open
          <svg className="h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        </span>
      </div>
    </div>
  );
}

/* ───── Campaign List (multi-campaign card grid, grouped by status) ───── */
function CampaignList({
  campaigns,
  onSelect,
  onNew,
  onEditCampaign,
  onDuplicateCampaign,
  onArchiveCampaign,
}: {
  campaigns: Campaign[];
  onSelect: (id: string) => void;
  onNew: () => void;
  onEditCampaign: (id: string, patch: Partial<Campaign>) => void;
  onDuplicateCampaign: (id: string) => void;
  onArchiveCampaign: (id: string) => void;
}) {
  const [editing, setEditing] = useState<Campaign | null>(null);
  const groups: { key: Campaign["status"]; label: string }[] = [
    { key: "active", label: "Active" },
    { key: "draft", label: "Draft" },
    { key: "planned", label: "Planned" },
    { key: "complete", label: "Complete" },
  ];

  const renderedGroups = groups
    .map((g) => ({ ...g, items: campaigns.filter((c) => c.status === g.key) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">Campaigns</h1>
      </div>

      {campaigns.length === 0 ? (
        /* Empty state — first-run for a new agency/client (UX, matches the app's other empty states). */
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-black/[0.10] bg-white/50 px-6 py-20 text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </div>
          <h2 className="text-[15px] font-semibold text-gray-900">No campaigns yet</h2>
          <p className="mt-1.5 max-w-sm text-[13px] leading-relaxed text-gray-500">
            Campaigns organize your deliverables across phases — from planning through launch. Create your first one
            to start building out work, or assign drafts to it from a meeting.
          </p>
          <button
            onClick={onNew}
            className="mt-5 rounded-lg bg-gray-900 px-4 py-2 text-[13px] font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.98]"
          >
            + New campaign
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {renderedGroups.map((g) => {
            return (
              <section key={g.key}>
                <div className="mb-3 flex items-center gap-2">
                  <h2 className="text-[13px] font-semibold text-gray-900">{g.label}</h2>
                  <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">{g.items.length}</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {g.items.map((c) => (
                    <CampaignCard
                      key={c.id}
                      c={c}
                      onSelect={onSelect}
                      onEdit={() => setEditing(c)}
                      onDuplicate={() => onDuplicateCampaign(c.id)}
                      onArchive={() => onArchiveCampaign(c.id)}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {editing && (
        <CampaignEditModal
          campaign={editing}
          onClose={() => setEditing(null)}
          onSave={(patch) => onEditCampaign(editing.id, patch)}
        />
      )}
    </div>
  );
}

/* ───── Inventory View (V4-style phase accordion) ───── */
function InventoryView({
  phases,
  campaignId,
  onAssetClick,
  onNewDeliverable,
  onEditAsset,
  onMoveAsset,
  onSetAssetForReview,
  onArchiveAsset,
}: {
  phases: Phase[];
  campaignId?: string;
  onAssetClick?: (phase: Phase, asset: Asset, index: number) => void;
  onNewDeliverable?: (phaseId: number) => void;
  onEditAsset?: (asset: Asset) => void;
  onMoveAsset?: (asset: Asset) => void;
  onSetAssetForReview?: (assetId: string) => void;
  onArchiveAsset?: (assetId: string) => void;
}) {
  // First phase open by default; each phase's expand/collapse state persists per campaign
  // (D-9), so reopening a campaign restores how you left its board. Filtering still
  // auto-expands matches (see isOpen).
  const phaseKey = `agents.campaignPhases.${campaignId || "default"}`;
  const [expanded, setExpanded] = useState<Record<number, boolean>>(() => {
    try { const saved = localStorage.getItem(phaseKey); if (saved) return JSON.parse(saved); } catch { /* ignore */ }
    return phases[0] ? { [phases[0].id]: true } : {};
  });
  const toggle = (id: number) => setExpanded((prev) => {
    const next = { ...prev, [id]: !prev[id] };
    try { localStorage.setItem(phaseKey, JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  // Asset filters (search / type / status / owner / due) — walkthrough ask. Filters within each phase.
  const [q, setQ] = useState("");
  const [typeF, setTypeF] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [ownerF, setOwnerF] = useState("all");
  const [dueF, setDueF] = useState("all");
  const typeOpts = Array.from(new Set(phases.flatMap((p) => p.assets.map((a) => a.type)))).sort();
  const ownerOpts = Array.from(new Set(phases.flatMap((p) => p.assets.map((a) => a.agent)).filter(Boolean))).sort();
  // Bucket an asset's due date relative to today for the due filter.
  const dueBucket = (due: string): "none" | "overdue" | "week" | "later" => {
    if (!due) return "none";
    const d = new Date(`${due}T00:00:00`);
    if (isNaN(d.getTime())) return "none";
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const diff = Math.round((d.getTime() - now.getTime()) / 86400000);
    if (diff < 0) return "overdue";
    if (diff <= 7) return "week";
    return "later";
  };
  const filtering = q.trim() !== "" || typeF !== "all" || statusF !== "all" || ownerF !== "all" || dueF !== "all";
  const displayPhases = phases.map((p) => ({
    ...p,
    assets: p.assets.filter(
      (a) =>
        (!q.trim() || a.name.toLowerCase().includes(q.toLowerCase())) &&
        (typeF === "all" || a.type === typeF) &&
        (statusF === "all" || a.status === statusF) &&
        (ownerF === "all" || a.agent === ownerF) &&
        (dueF === "all" || dueBucket(a.due) === dueF)
    ),
  }));
  const FSEL = "rounded-lg border border-black/[0.06] px-2 py-1.5 text-xs text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search assets…" className={`${FSEL} flex-1 min-w-[160px]`} />
        <select value={typeF} onChange={(e) => setTypeF(e.target.value)} className={FSEL}>
          <option value="all">All types</option>
          {typeOpts.map((t) => (<option key={t} value={t}>{t}</option>))}
        </select>
        <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className={FSEL}>
          <option value="all">All statuses</option>
          <option value="to-draft">To draft</option>
          <option value="drafted">Drafted</option>
          <option value="pending-review">Pending review</option>
          <option value="secured">Secured</option>
          <option value="blocked">Blocked</option>
        </select>
        <select value={ownerF} onChange={(e) => setOwnerF(e.target.value)} className={FSEL}>
          <option value="all">All owners</option>
          {ownerOpts.map((o) => (<option key={o} value={o}>{o}</option>))}
        </select>
        <select value={dueF} onChange={(e) => setDueF(e.target.value)} className={FSEL}>
          <option value="all">Any due date</option>
          <option value="overdue">Overdue</option>
          <option value="week">Due this week</option>
          <option value="later">Due later</option>
          <option value="none">No due date</option>
        </select>
      </div>
      {displayPhases.map((phase) => {
        if (filtering && phase.assets.length === 0) return null;
        const isOpen = expanded[phase.id] || filtering; // auto-expand matches while filtering
        const pct = Math.round((phase.secured / phase.total) * 100);
        const isComplete = phase.secured === phase.total;

        return (
          <div key={phase.id} className="overflow-hidden rounded-xl border border-black/[0.06] bg-white shadow-sm ring-1 ring-black/[0.04]">
            <button onClick={() => toggle(phase.id)} className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-all duration-150 hover:bg-gray-50/60">
              <div className={`h-10 w-1 shrink-0 rounded-full ${phase.colorBar}`} />
              <svg className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-150 ${isOpen ? "rotate-90" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-semibold text-gray-900">{phase.name}</span>
                  {isComplete && (
                    <svg className="h-4 w-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                </div>
                <p className="text-xs text-gray-400">{phase.dates}</p>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="hidden h-1.5 w-20 overflow-hidden rounded-full bg-gray-100 sm:block">
                  <div className={`h-full rounded-full ${phase.colorBar} transition-all duration-500`} style={{ width: `${pct}%` }} />
                </div>
                <span className="whitespace-nowrap text-xs font-medium text-gray-500">{phase.secured}/{phase.total}</span>
              </div>
            </button>

            {isOpen && (
              <div className="border-t border-black/[0.06]">
                <div className="grid grid-cols-[1fr_80px_120px_72px_72px_32px] gap-2 px-5 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  <span>Asset</span><span>Type</span><span>Status</span><span>Owner</span><span className="text-right">Due</span><span />
                </div>
                {phase.assets.map((a, i) => {
                  const chip = STATUS_CHIP[a.status];
                  const badge = TYPE_BADGE[a.type] || "bg-gray-50 text-gray-600 ring-1 ring-gray-200/50";
                  const inReview = a.reviewState === "in-review";
                  return (
                    <div
                      key={a.id}
                      onClick={() => onAssetClick?.(phase, a, i)}
                      className={`group/row grid cursor-pointer grid-cols-[1fr_80px_120px_72px_72px_32px] items-center gap-2 border-t border-black/[0.03] px-5 py-2.5 transition-all duration-150 hover:bg-gray-50/50 ${inReview ? "bg-amber-50/40" : ""}`}
                    >
                      <div className="flex min-w-0 items-center gap-1.5">
                        {inReview && (
                          <span title="In review" className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                            <svg className="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </span>
                        )}
                        <span className="truncate text-[13px] text-gray-800">{a.name}</span>
                        {a.meetingSource && (
                          <span className="ml-1 shrink-0 text-[10px] text-gray-300">from {a.meetingSource}</span>
                        )}
                      </div>
                      <span><span className={`inline-block rounded-md px-2 py-0.5 text-[10px] font-semibold ${badge}`}>{a.type}</span></span>
                      <span><span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${chip.classes}`}>{chip.label}</span></span>
                      <span className="text-xs text-gray-500">{a.agent}</span>
                      <span className="text-right text-xs text-gray-400">{a.due}</span>
                      <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                        <div className="opacity-0 transition-opacity duration-150 group-hover/row:opacity-100">
                          <OverflowMenu
                            alwaysVisible
                            items={[
                              { label: "Edit", onClick: () => onEditAsset?.(a) },
                              { label: "Move to phase", onClick: () => onMoveAsset?.(a) },
                              { label: "Send for review", onClick: () => onSetAssetForReview?.(a.id) },
                              { label: "Archive", onClick: () => onArchiveAsset?.(a.id), destructive: true },
                            ]}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
                <button
                  type="button"
                  onClick={() => onNewDeliverable?.(phase.id)}
                  className="flex w-full items-center gap-1.5 border-t border-black/[0.03] px-5 py-2.5 text-left text-[12px] font-medium text-gray-400 transition-all duration-150 hover:bg-gray-50/60 hover:text-gray-700"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  New Deliverable
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ───── Timeline View (Gantt-style) ───── */
function TimelineView({ phases, rawPhases, onManage }: { phases: Phase[]; rawPhases: PhaseDef[]; onManage: () => void }) {
  // Real schedule from each phase's actual dates (set in Manage phases). Bar position
  // and width are computed from the campaign's real min-start → max-end span.
  const datesFor = (slug?: string) => rawPhases.find((p) => p.id === slug)?.dates || {};
  const ts = (s?: string) => (s ? new Date(`${s}T00:00:00`).getTime() : NaN);
  const dated = rawPhases.filter((p) => p.dates?.start && p.dates?.end);
  const min = dated.length ? Math.min(...dated.map((p) => ts(p.dates!.start))) : NaN;
  const max = dated.length ? Math.max(...dated.map((p) => ts(p.dates!.end))) : NaN;
  const span = max - min || 1;
  const pos = (t: number) => Math.max(0, Math.min(100, ((t - min) / span) * 100));

  return (
    <div className="rounded-xl border border-black/[0.06] bg-white shadow-sm ring-1 ring-black/[0.04]">
      <div className="flex items-center justify-between border-b border-black/[0.06] px-4 py-3">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Phase schedule</span>
        <div className="flex items-center gap-3">
          {dated.length > 0 && (
            <span className="text-[10px] text-gray-400">{fmtCampaignDate(new Date(min).toISOString().slice(0, 10))} – {fmtCampaignDate(new Date(max).toISOString().slice(0, 10))}</span>
          )}
          <button onClick={onManage} className="text-[11px] font-medium text-blue-600 hover:underline">Manage phases</button>
        </div>
      </div>

      {dated.length === 0 && (
        <p className="px-4 py-6 text-center text-xs text-gray-400">No phase dates yet — set start &amp; end dates in <button onClick={onManage} className="font-medium text-blue-600 hover:underline">Manage phases</button> to see the schedule.</p>
      )}

      {dated.length > 0 && phases.map((phase) => {
        const d = datesFor(phase.slug);
        const has = !!(d.start && d.end);
        const left = has ? pos(ts(d.start)) : 0;
        const width = has ? Math.max(2, pos(ts(d.end)) - left) : 0;
        const pct = phase.total ? Math.round((phase.secured / phase.total) * 100) : 0;
        return (
          <div key={phase.id} className="flex border-b border-gray-50 last:border-0">
            <div className="flex w-[220px] shrink-0 items-center gap-2.5 border-r border-black/[0.06] px-4 py-4">
              {/* BUG-15: an "Unphased" bucket with assets is a data problem (a deliverable
                  whose phase doesn't match any defined phase) — surface it instead of letting
                  it sit silently. */}
              {(() => {
                const unphasedWarn = phase.name === "Unphased" && phase.total > 0;
                return (
                  <>
                    <div className={`h-3 w-3 shrink-0 rounded-full ${unphasedWarn ? "bg-amber-400" : phase.colorBar}`} />
                    <div className="min-w-0">
                      <p className={`truncate text-xs font-medium ${unphasedWarn ? "text-amber-700" : "text-gray-900"}`}>
                        {unphasedWarn ? `⚠ ${phase.name}` : phase.name}
                      </p>
                      <p className={`text-[10px] ${unphasedWarn ? "text-amber-600" : "text-gray-400"}`}>
                        {unphasedWarn
                          ? `${phase.total} asset${phase.total > 1 ? "s" : ""} not matched to a phase`
                          : `${has ? `${fmtCampaignDate(d.start!)} – ${fmtCampaignDate(d.end!)}` : "No dates set"} · ${phase.secured}/${phase.total} assets`}
                      </p>
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="flex flex-1 items-center px-3">
              <div className="relative h-8 w-full">
                {has ? (
                  <>
                    <div className={`absolute top-1 h-6 rounded-lg ${phase.colorBar} opacity-20`} style={{ left: `${left}%`, width: `${width}%` }} />
                    <div className={`absolute top-1 h-6 rounded-lg ${phase.colorBar} transition-all duration-500`} style={{ left: `${left}%`, width: `${(pct / 100) * width}%` }} />
                    <span className="absolute top-2 text-[10px] font-semibold text-gray-600" style={{ left: `calc(${left}% + 4px)` }}>{pct}%</span>
                  </>
                ) : (
                  <span className="absolute top-2 text-[10px] italic text-gray-300">no dates</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ───── Story View (narrative with rationale) ───── */
function StoryView({ phases }: { phases: Phase[] }) {
  return (
    <div className="space-y-8">
      {phases.map((phase) => (
        <div key={phase.id}>
          <div className="mb-3 flex items-center gap-2.5">
            <div className={`h-3 w-3 rounded-full ${phase.colorBar}`} />
            <h3 className="text-[13px] font-semibold text-gray-900">{phase.name}</h3>
            <span className="text-xs text-gray-400">{phase.dates}</span>
          </div>
          <div className="space-y-2">
            {phase.assets.map((a, i) => (
              <div key={i} className="rounded-xl border border-black/[0.06] bg-white px-4 py-3 shadow-sm ring-1 ring-black/[0.04] transition-all duration-150 hover:border-black/10 hover:shadow">
                <div className="flex items-center gap-2">
                  <span className={`rounded-md px-2 py-0.5 text-[10px] font-semibold ${TYPE_BADGE[a.type] || "bg-gray-50 text-gray-600 ring-1 ring-gray-200/50"}`}>{a.type}</span>
                  <span className="text-[13px] font-medium text-gray-900">{a.name}</span>
                  <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_CHIP[a.status].classes}`}>{STATUS_CHIP[a.status].label}</span>
                </div>
                {a.rationale && (
                  <p className="mt-1.5 text-xs text-gray-500 italic">&ldquo;{a.rationale}&rdquo;</p>
                )}
                {a.meetingSource && (
                  <p className="mt-1 text-[10px] text-gray-400">
                    <span className="text-gray-300">Source:</span> {a.meetingSource}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ───── Campaign Detail (portal hub) ───── */
function CampaignDetail({
  campaign,
  onBack,
  onEditCampaign,
  onSavePhases,
  onAddDeliverable,
  onBindExisting,
  onEditAsset,
  onSetAssetForReview,
  onArchiveAsset,
}: {
  campaign: Campaign;
  onBack: () => void;
  onEditCampaign: (patch: Partial<Campaign>) => void;
  onSavePhases: (phases: PhaseDef[]) => void;
  onAddDeliverable: (phaseId: number, seed: SeedAsset) => void;
  onBindExisting: (deliverableId: string, phaseId: number) => void;
  onEditAsset: (assetId: string, patch: Partial<Asset>) => void;
  onSetAssetForReview: (assetId: string) => void;
  onArchiveAsset: (assetId: string) => void;
}) {
  const [view, setView] = useState<ViewMode>("assets");
  const [managingPhases, setManagingPhases] = useState(false);
  // UX-C2: Foundation is shown by default (not hidden behind a click).
  const [showFoundation, setShowFoundation] = useState(true);
  const [foundationPickOpen, setFoundationPickOpen] = useState(false);
  // UX-C7: the header phase list starts collapsed; a toggle expands it. Keeps the
  // header compact when a campaign has many (10+) phases.
  const [phasesOpen, setPhasesOpen] = useState(false);
  // D4: pin KB docs as this campaign's foundation (campaign.foundation[]).
  const addFoundationDocs = (ids: string[]) => {
    const merged = Array.from(new Set([...(campaign.foundation || []), ...ids]));
    fetch(`${DOCS_API_DOMAIN}/api/agents/entity/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: CAMP_CLIENT_SLUG, type: "campaign", id: campaign.id, fields: { foundation: merged } }),
    }).catch(() => {});
    onEditCampaign({ foundation: merged }); // optimistic local
    // reflect immediately: re-fetch deliverables, keep the pinned ones
    fetch(`${DOCS_API_DOMAIN}/api/agents/deliverables?client=${CAMP_CLIENT_SLUG}`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => {
        const f = new Set(merged);
        setRelFoundation((d.items || []).filter((x: { id: string }) => f.has(x.id)).map((x: { id: string; title: string; preview?: string }) => ({ id: x.id, label: x.title, snippet: x.preview || "" })));
      })
      .catch(() => {});
    setFoundationPickOpen(false);
  };
  const [showHub, setShowHub] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<{ phase: Phase; asset: Asset; index: number } | null>(null);
  const [assetFullPage, setAssetFullPage] = useState(false);
  // Real content for the open asset (the deliverable's markdown body), fetched on open.
  const [assetBody, setAssetBody] = useState<string | null>(null);
  const [assetBodyLoading, setAssetBodyLoading] = useState(false);
  useEffect(() => {
    if (!selectedAsset) { setAssetBody(null); return; }
    let cancelled = false;
    setAssetBodyLoading(true);
    setAssetBody(null);
    fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/${encodeURIComponent(selectedAsset.asset.id)}?client=${CAMP_CLIENT_SLUG}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setAssetBody(typeof d?.body === "string" ? d.body : ""); })
      .catch(() => { if (!cancelled) setAssetBody(""); })
      .finally(() => { if (!cancelled) setAssetBodyLoading(false); });
    return () => { cancelled = true; };
  }, [selectedAsset?.asset.id]);
  // CRUD modal state
  const [newDeliverablePhase, setNewDeliverablePhase] = useState<number | null>(null);
  const [linkExistingPhase, setLinkExistingPhase] = useState<number | null>(null);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [movingAsset, setMovingAsset] = useState<Asset | null>(null);

  // Related-panel real data: pending reviews for this campaign, recent meetings,
  // and the client's Foundation docs. Fetched on demand (self-contained).
  const [relReviews, setRelReviews] = useState<{ title: string; type: string; status: string }[]>([]);
  const [relMeetings, setRelMeetings] = useState<{ id: string; date: string; title: string; deliverables: number }[]>([]);
  const [relFoundation, setRelFoundation] = useState<{ id: string; label: string; snippet: string }[]>([]);
  useEffect(() => {
    let cancelled = false;
    const base = `${DOCS_API_DOMAIN}/api/agents`;
    fetch(`${base}/reviews?client=${CAMP_CLIENT_SLUG}`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => {
        if (cancelled) return;
        const mapState = (rs: string) => (rs === "rejected" || rs === "changes-requested" ? "Blocked" : "Pending Review");
        setRelReviews(
          (d.items || [])
            .filter((x: { campaign?: string }) => x.campaign === campaign.id)
            .map((x: { title: string; deliverableType?: string; reviewState?: string }) => ({
              title: x.title,
              type: DTYPE_TO_BADGE[x.deliverableType || "doc"] || "Doc",
              status: mapState(x.reviewState || ""),
            }))
        );
      })
      .catch(() => {});
    fetch(`${base}/meetings?client=${CAMP_CLIENT_SLUG}&campaign=${encodeURIComponent(campaign.id)}`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => {
        if (cancelled) return;
        setRelMeetings(
          (d.items || []).slice(0, 4).map((m: { id: string; date?: string; title: string; deliverableCount?: number }) => ({
            id: m.id,
            date: m.date ? new Date(m.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "",
            title: m.title,
            deliverables: m.deliverableCount || 0,
          }))
        );
      })
      .catch(() => {});
    // Per-campaign foundation (D4): show the docs this campaign pins (campaign.foundation[]),
    // not the global foundation set. Fetch all deliverables, filter to the pinned ids.
    fetch(`${base}/deliverables?client=${CAMP_CLIENT_SLUG}`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => {
        if (cancelled) return;
        const fids = new Set(campaign.foundation || []);
        setRelFoundation(
          (d.items || [])
            .filter((x: { id: string }) => fids.has(x.id))
            .map((x: { id: string; title: string; preview?: string }) => ({
              id: x.id,
              label: x.title,
              snippet: x.preview || "",
            }))
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [campaign.id]);

  const phaseTotal = campaign.phases.reduce((s, p) => s + p.total, 0);
  const phaseSecured = campaign.phases.reduce((s, p) => s + p.secured, 0);
  const totalAssets = phaseTotal || campaign.assetsTotal || 0;
  const totalSecured = phaseTotal ? phaseSecured : (campaign.assetsSecured || 0);

  const views: { key: ViewMode; label: string }[] = [
    { key: "assets", label: "Assets" },
    { key: "schedule", label: "Schedule" },
    // Rationale tab cut for trial (no real source content) — re-add post-trial.
  ];

  return (
    <div className="mx-auto max-w-6xl px-6 py-6">
      {/* Back */}
      <button onClick={onBack} className="mb-5 flex items-center gap-1.5 text-xs font-medium text-gray-400 transition-all duration-150 hover:text-gray-600">
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
        All Campaigns
      </button>

      {/* Campaign Header */}
      <div className="mb-6 flex items-start gap-5 rounded-xl border border-black/[0.06] bg-white p-6 shadow-sm ring-1 ring-black/[0.04]">
        <ProgressCircle percent={campaign.progress} size={80} strokeWidth={6} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5">
            <h1 className="text-xl font-bold tracking-tight text-gray-900">{campaign.name}</h1>
            <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${CAMPAIGN_STATUS[campaign.status].classes}`}>
              {CAMPAIGN_STATUS[campaign.status].label}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-gray-400">{campaign.dates}</p>
          <p className="mt-1.5 text-[13px] leading-snug text-gray-600">{campaign.promise}</p>

          {/* Pending reviews bar + distinct progress stats. "% Done" and the asset
              COUNT are surfaced as two separate, clearly-labeled stats rather than a
              single conflated number (UX-C4). */}
          <div className="mt-3.5 flex flex-wrap items-center gap-3">
            <Link to="/agents/reviews" className="flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200/50 transition-all duration-150 hover:bg-amber-100">
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" />
              </svg>
              {relReviews.length} pending reviews
            </Link>
            <div className="flex items-center gap-1.5 rounded-lg bg-gray-50 px-2.5 py-1 ring-1 ring-black/[0.04]">
              <span className="text-[9px] font-semibold uppercase tracking-wider text-gray-400">% Done</span>
              <span className="text-[12px] font-bold text-gray-900">{campaign.progress}%</span>
            </div>
            <div className="flex items-center gap-1.5 rounded-lg bg-gray-50 px-2.5 py-1 ring-1 ring-black/[0.04]">
              <span className="text-[9px] font-semibold uppercase tracking-wider text-gray-400">Assets</span>
              <span className="text-[12px] font-bold text-gray-900">{totalAssets}</span>
              <span className="text-[10px] text-gray-400">({totalSecured} secured)</span>
            </div>
          </div>
        </div>

        {/* Phase legend — collapsed by default (UX-C7); scroll-constrained so a long
            phase list never blows out the header layout (UX-C6). */}
        {campaign.phases.length > 0 && (
        <div className="shrink-0 w-[180px]">
          <button
            type="button"
            onClick={() => setPhasesOpen((o) => !o)}
            aria-expanded={phasesOpen}
            className="mb-2.5 flex w-full items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 transition-colors hover:text-gray-600"
          >
            <svg className={`h-3 w-3 shrink-0 transition-transform duration-150 ${phasesOpen ? "rotate-90" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
            <span>Phases</span>
            <span className="ml-auto rounded-full bg-gray-100 px-1.5 py-0.5 text-[9px] font-semibold text-gray-500">{campaign.phases.length}</span>
          </button>
          {phasesOpen && (
            <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
              {campaign.phases.map((p) => {
                const pct = p.total ? Math.round((p.secured / p.total) * 100) : 0;
                return (
                  <div key={p.id} className="flex h-5 items-center gap-2">
                    <div className={`h-2.5 w-2.5 shrink-0 rounded-full ${p.colorBar}`} />
                    <span className="flex-1 truncate text-xs text-gray-700" title={p.name}>{p.name}</span>
                    <div className="h-1 w-12 shrink-0 overflow-hidden rounded-full bg-gray-100">
                      <div className={`h-full rounded-full ${p.colorBar} transition-all duration-500`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-6 shrink-0 text-right text-[10px] text-gray-400">{pct}%</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}
      </div>

      {/* View toggle + expandable sections */}
      <div className="mb-5 flex items-center justify-between">
        <div className="flex gap-0.5 rounded-lg bg-gray-100/80 p-0.5">
          {views.map((v) => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              className={`rounded-md px-3.5 py-1.5 text-xs font-medium transition-all duration-150 ${
                view === v.key ? "bg-gray-900 text-white shadow-sm" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFoundation(!showFoundation)}
            className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-all duration-150 ${
              showFoundation ? "border-blue-200 bg-blue-50 text-blue-700 shadow-sm" : "border-black/[0.06] text-gray-500 hover:text-gray-700 hover:bg-gray-50 hover:border-black/10"
            }`}
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
            Foundation
          </button>
          <button
            onClick={() => setShowHub(!showHub)}
            className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-all duration-150 ${
              showHub ? "border-blue-200 bg-blue-50 text-blue-700 shadow-sm" : "border-black/[0.06] text-gray-500 hover:text-gray-700 hover:bg-gray-50 hover:border-black/10"
            }`}
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
            </svg>
            Related
          </button>
          {/* Funnel hidden for trial (not wired) — re-enable post-trial. */}
          <button
            onClick={() => setManagingPhases(true)}
            className="flex items-center gap-1.5 rounded-lg border border-black/[0.06] px-3 py-1.5 text-xs font-medium text-gray-500 transition-all duration-150 hover:border-black/10 hover:bg-gray-50 hover:text-gray-700"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" /></svg>
            Manage phases
          </button>
        </div>
      </div>

      {/* Foundation panel (progressive disclosure) */}
      {showFoundation && (
        <div className="mb-5 rounded-xl border border-blue-100/80 bg-blue-50/20 p-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Strategic Foundation</h3>
            <button data-testid="add-foundation" onClick={() => setFoundationPickOpen(true)} className="text-[11px] font-medium text-blue-600 hover:underline">+ Add doc</button>
          </div>
          {(() => {
            // Resolve campaign.foundation[] (an array of deliverable ids) to linked
            // docs (UX-C2). Ids whose titles the API returned render with a title +
            // snippet; any id NOT resolved still renders as a deep link by id with a
            // readable fallback label, so a pinned doc is never silently dropped.
            const resolved = new Map(relFoundation.map((s) => [s.id, s]));
            const cards = (campaign.foundation || []).map(
              (id) => resolved.get(id) || { id, label: `Document ${id}`, snippet: "" }
            );
            if (cards.length === 0) {
              return <p className="text-xs text-gray-400">No Foundation docs for this campaign yet — add one.</p>;
            }
            return (
              <div className="grid grid-cols-3 gap-3">
                {cards.map((snap) => (
                  <Link
                    key={snap.id}
                    to={`/agents/knowledge-base?id=${encodeURIComponent(snap.id)}`}
                    className="rounded-xl border border-black/[0.06] bg-white p-3.5 shadow-sm ring-1 ring-black/[0.04] transition-all duration-150 hover:border-black/10 hover:shadow"
                  >
                    <p className="text-xs font-semibold text-gray-900">{snap.label}</p>
                    {snap.snippet
                      ? <p className="mt-1 text-xs text-gray-500 line-clamp-2">{stripMd(snap.snippet)}</p>
                      : <p className="mt-1 text-xs text-gray-300">Open document →</p>}
                  </Link>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Related panel (portal hub links) */}
      {showHub && (
        <div className="mb-5 grid grid-cols-2 gap-3">
          {/* Pending reviews */}
          <div className="rounded-xl border border-black/[0.06] bg-white p-4 shadow-sm ring-1 ring-black/[0.04]">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-xs font-semibold text-gray-900">Pending Reviews</h3>
              <Link to="/agents/reviews" className="text-[10px] text-gray-400 transition-all duration-150 hover:text-gray-600">View all</Link>
            </div>
            <div className="space-y-2">
              {relReviews.length === 0 && <p className="text-xs text-gray-400">No pending reviews for this campaign.</p>}
              {relReviews.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-[13px]">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${r.status === "Blocked" ? "bg-red-50 text-red-700 ring-1 ring-red-200/50" : "bg-amber-50 text-amber-700 ring-1 ring-amber-200/50"}`}>{r.status}</span>
                  <span className="flex-1 text-gray-700">{r.title}</span>
                  <span className="text-xs text-gray-400">{r.type}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent meetings */}
          <div className="rounded-xl border border-black/[0.06] bg-white p-4 shadow-sm ring-1 ring-black/[0.04]">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-xs font-semibold text-gray-900">Recent Meetings</h3>
              <Link to="/agents/meetings" className="text-[10px] text-gray-400 transition-all duration-150 hover:text-gray-600">View all</Link>
            </div>
            <div className="space-y-2">
              {relMeetings.length === 0 && <p className="text-xs text-gray-400">No recent meetings.</p>}
              {relMeetings.map((m, i) => (
                <Link key={i} to={`/agents/digest?id=${encodeURIComponent(m.id)}`} className="flex items-center gap-2 rounded-md px-1.5 py-1 text-[13px] transition-colors hover:bg-gray-50">
                  <span className="text-gray-400 w-[50px] shrink-0 text-xs">{m.date}</span>
                  <span className="flex-1 truncate text-blue-600 hover:underline">{m.title}</span>
                  <span className="text-xs text-gray-400">{m.deliverables} deliverables</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Asset views */}
      {campaign.phases.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-black/[0.06] bg-gray-50/40 py-16 text-center ring-1 ring-black/[0.04]">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-400">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l2.25 2.25L15 12m-9 5.25h12a2.25 2.25 0 002.25-2.25V9.75a9 9 0 00-9-9H5.625c-.621 0-1.125.504-1.125 1.125v15.75c0 .621.504 1.125 1.125 1.125z" />
            </svg>
          </span>
          <p className="text-[13px] font-semibold text-gray-700">No phases planned yet</p>
          <p className="max-w-sm text-xs text-gray-400">This campaign hasn&rsquo;t been broken into phases and assets. Next milestone: {campaign.nextMilestone ? `${campaign.nextMilestone.label} · ${campaign.nextMilestone.date}` : "kickoff"}.</p>
          <button onClick={() => setManagingPhases(true)} className="mt-2 flex items-center gap-1.5 rounded-lg bg-gray-900 px-3.5 py-2 text-xs font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.97]">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Plan phases
          </button>
        </div>
      ) : (
        <>
          {view === "assets" && (
            <InventoryView
              phases={campaign.phases}
              campaignId={campaign.id}
              onAssetClick={(phase, asset, index) => { setSelectedAsset({ phase, asset, index }); setAssetFullPage(false); }}
              onNewDeliverable={(phaseId) => setNewDeliverablePhase(phaseId)}
              onEditAsset={(asset) => setEditingAsset(asset)}
              onMoveAsset={(asset) => setMovingAsset(asset)}
              onSetAssetForReview={(assetId) => onSetAssetForReview(assetId)}
              onArchiveAsset={(assetId) => onArchiveAsset(assetId)}
            />
          )}
          {view === "schedule" && <TimelineView phases={campaign.phases} rawPhases={campaign.rawPhases} onManage={() => setManagingPhases(true)} />}
          {view === "rationale" && <StoryView phases={campaign.phases} />}
        </>
      )}

      {/* Asset slide-out panel */}
      {selectedAsset && (
        <>
          <div className="fixed inset-0 z-40 bg-black/15 backdrop-blur-[2px] transition-opacity duration-200" onClick={() => setSelectedAsset(null)} />
          <div className={`fixed right-0 top-0 z-50 flex h-full flex-col border-l border-black/[0.06] bg-white shadow-2xl transition-[width] duration-200 ${assetFullPage ? "w-[80vw]" : "w-[60vw]"}`}>
            {/* Refined header */}
            <div className="flex items-center justify-between border-b border-black/[0.06] px-6 py-4">
              <div className="min-w-0 flex-1">
                <h2 className="text-[13px] font-bold text-gray-900 truncate">{selectedAsset.asset.name}</h2>
                <p className="mt-0.5 text-xs text-gray-400">{selectedAsset.phase.name} &middot; {selectedAsset.asset.due}</p>
              </div>
              <div className="ml-3 flex items-center gap-1">
                {/* AMB-404: cycle left/right through the assets in this phase, like the other drawers. */}
                {(() => {
                  const list = selectedAsset.phase.assets;
                  const cur = list.findIndex((a) => a.id === selectedAsset.asset.id);
                  const go = (dir: number) => {
                    const ni = cur + dir;
                    if (ni >= 0 && ni < list.length) setSelectedAsset({ phase: selectedAsset.phase, asset: list[ni], index: ni });
                  };
                  return (
                    <div className="mr-1 flex items-center gap-0.5">
                      <button onClick={() => go(-1)} disabled={cur <= 0} title="Previous asset in phase" aria-label="Previous asset" className="rounded-lg p-1.5 text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
                      </button>
                      <span className="select-none px-0.5 text-[11px] tabular-nums text-gray-400">{cur + 1} / {list.length}</span>
                      <button onClick={() => go(1)} disabled={cur >= list.length - 1} title="Next asset in phase" aria-label="Next asset" className="rounded-lg p-1.5 text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                      </button>
                    </div>
                  );
                })()}
                <button
                  onMouseEnter={(e) => { const tip = e.currentTarget.querySelector('.tooltip') as HTMLElement; if (tip) tip.style.display = 'block'; }}
                  onMouseLeave={(e) => { const tip = e.currentTarget.querySelector('.tooltip') as HTMLElement; if (tip) tip.style.display = 'none'; }}
                  onClick={() => setAssetFullPage(!assetFullPage)}
                  className="relative rounded-lg p-1.5 text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    {assetFullPage ? (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                    )}
                  </svg>
                  <span className="tooltip absolute -bottom-8 right-0 hidden whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-[10px] text-white shadow-lg">Open as full page</span>
                </button>
                <button onClick={() => setSelectedAsset(null)} className="rounded-lg p-1.5 text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto px-6 py-5">
              <div className="mb-5 flex flex-wrap items-center gap-2">
                <span className={`rounded-md px-2 py-0.5 text-[10px] font-semibold ${TYPE_BADGE[selectedAsset.asset.type] || "bg-gray-50 text-gray-600 ring-1 ring-gray-200/50"}`}>{selectedAsset.asset.type}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_CHIP[selectedAsset.asset.status].classes}`}>{STATUS_CHIP[selectedAsset.asset.status].label}</span>
              </div>
              <div className="mb-5 space-y-2.5">
                <div className="flex items-center gap-2 text-[13px]">
                  <span className="w-16 shrink-0 text-gray-400 text-xs">Owner</span>
                  <span className="font-medium text-gray-700">{selectedAsset.asset.agent}</span>
                </div>
                <div className="flex items-center gap-2 text-[13px]">
                  <span className="w-16 shrink-0 text-gray-400 text-xs">Due</span>
                  <span className="font-medium text-gray-700">{selectedAsset.asset.due}</span>
                </div>
                <div className="flex items-center gap-2 text-[13px]">
                  <span className="w-16 shrink-0 text-gray-400 text-xs">Phase</span>
                  <span className="font-medium text-gray-700">{selectedAsset.phase.name}</span>
                </div>
              </div>
              {selectedAsset.asset.rationale && (
                <div className="mb-5 rounded-xl bg-gray-50 p-4 ring-1 ring-black/[0.04]">
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Rationale</p>
                  <p className="text-[13px] leading-relaxed text-gray-600 italic">&ldquo;{selectedAsset.asset.rationale}&rdquo;</p>
                </div>
              )}
              {selectedAsset.asset.meetingSource && (
                <div className="mb-5 flex items-center gap-1.5 text-xs text-gray-400">
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.07-9.07l-1.757 1.757" />
                  </svg>
                  Source: {selectedAsset.asset.meetingSource}
                </div>
              )}
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Content</p>
                  <Link to={`/agents/transcript-view?id=${encodeURIComponent(selectedAsset.asset.id)}`} className="text-[11px] font-medium text-blue-600 hover:underline">Open full document →</Link>
                </div>
                {selectedAsset.asset.sourceUrl && (
                  <a href={selectedAsset.asset.sourceUrl} target="_blank" rel="noreferrer" className="mb-2 inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-2.5 py-1 text-[11px] font-medium text-blue-700 ring-1 ring-blue-200/50 transition-all hover:bg-blue-100">
                    View external content ↗
                  </a>
                )}
                {assetBodyLoading ? (
                  <p className="text-[13px] text-gray-400">Loading content…</p>
                ) : assetBody && assetBody.replace(/^\s*#.*$/m, "").trim() ? (
                  <div className="rounded-xl border border-black/[0.06] bg-white p-4 text-[13px] leading-relaxed text-gray-700 ring-1 ring-black/[0.04] [&_h1]:mb-2 [&_h1]:text-base [&_h1]:font-bold [&_h2]:mb-1.5 [&_h2]:mt-3 [&_h2]:text-sm [&_h2]:font-semibold [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-0.5">
                    <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} rehypePlugins={[rehypeRaw]} components={markdownComponents}>{assetBody}</Markdown>
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-black/[0.12] bg-gray-50/50 p-5 text-center ring-1 ring-black/[0.04]">
                    <p className="text-[13px] text-gray-500">This asset doesn&rsquo;t have content yet.</p>
                    <Link to={`/agents/transcript-view?id=${encodeURIComponent(selectedAsset.asset.id)}`} className="mt-2.5 inline-block rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white transition-all hover:bg-gray-800">Open in editor to add content</Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* CRUD modals */}
      {/* Campaign edit modal lives in the parent now (triggered from the top-bar
          "Campaign actions" menu) — see CampaignsScreen. */}
      {managingPhases && (
        <ManagePhasesModal
          initial={campaign.rawPhases}
          counts={Object.fromEntries(campaign.phases.filter((p) => p.slug).map((p) => [p.slug as string, p.total]))}
          onClose={() => setManagingPhases(false)}
          onSave={onSavePhases}
        />
      )}
      {foundationPickOpen && (
        <EntityPicker
          title="Add a foundation doc to this campaign"
          endpoint={`/deliverables?client=${CAMP_CLIENT_SLUG}`}
          mapItem={(d) => ({ id: d.id, label: d.title || d.id, sublabel: d.deliverableType })}
          multiple
          selected={campaign.foundation || []}
          confirmLabel="Add to foundation"
          onConfirm={addFoundationDocs}
          onClose={() => setFoundationPickOpen(false)}
        />
      )}
      {newDeliverablePhase !== null && (
        <NewDeliverableModal
          onClose={() => setNewDeliverablePhase(null)}
          onCreate={(seed) => onAddDeliverable(newDeliverablePhase, seed)}
          onLinkExisting={() => { const ph = newDeliverablePhase; setNewDeliverablePhase(null); setLinkExistingPhase(ph); }}
        />
      )}
      {linkExistingPhase !== null && (
        <EntityPicker
          title="Link an existing document to this phase"
          endpoint={`/deliverables?client=${CAMP_CLIENT_SLUG}`}
          mapItem={(d) => ({ id: d.id, label: d.title || d.id, sublabel: d.campaign ? `in ${d.campaign}` : (d.deliverableType || "doc") })}
          confirmLabel="Link to campaign"
          onConfirm={(ids) => { if (ids[0]) onBindExisting(ids[0], linkExistingPhase); setLinkExistingPhase(null); }}
          onClose={() => setLinkExistingPhase(null)}
        />
      )}
      {editingAsset && (
        <DeliverableEditModal
          asset={editingAsset}
          onClose={() => setEditingAsset(null)}
          onSave={(patch) => onEditAsset(editingAsset.id, patch)}
        />
      )}
      {movingAsset && (
        <MovePhaseModal
          asset={movingAsset}
          phases={campaign.rawPhases}
          onClose={() => setMovingAsset(null)}
          onMove={(phaseId) => onEditAsset(movingAsset.id, { phase: phaseId })}
        />
      )}
    </div>
  );
}

/* ───── Page ───── */
type ToastState = { message: string; undo: () => void } | null;

export function Campaigns() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingCampaign, setEditingCampaign] = useState(false); // edit modal, triggered from the top-bar Campaign actions menu
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [archived, setArchived] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  // Pick reviewer(s) when setting an asset for review (routes it on the Reviews dashboard).
  const [reviewPickFor, setReviewPickFor] = useState<{ campaignId: string; assetId: string } | null>(null);
  // Optional Situation / Impact / prepended statement the submitter adds for context (B2).
  // The review screen shows them in order: Situation → Impact → Recommendation (= the asset).
  const [sendSir, setSendSir] = useState({ situation: "", impact: "", statement: "" });
  const [toast, setToast] = useState<ToastState>(null);
  // AMB-420: the list reacts to the LIVE top-bar client (was a module-frozen slug → the list
  // got stuck on whichever client was active when the screen first loaded, e.g. Valley Bank,
  // even with "All Clients" selected; only a switch-and-back reload fixed it).
  const activeSlug = useActiveClient().slug;

  // Load real campaigns + their deliverables (assets) from the operator API, scoped to the
  // active client. Deliverables stay "all" (resolved by id) so a campaign's assets always load.
  const loadCampaigns = (slug: string) =>
    Promise.all([
      fetch(`${DOCS_API_DOMAIN}/api/agents/campaigns?client=${slug}`).then((r) => (r.ok ? r.json() : Promise.reject(r.status))),
      fetch(`${DOCS_API_DOMAIN}/api/agents/deliverables?client=all`).then((r) => (r.ok ? r.json() : Promise.reject(r.status))),
    ])
      .then(([camps, dels]) => {
        // Empty for this client → clear (don't keep the previous client's campaigns).
        if (!camps?.items?.length) { setCampaigns([]); return; }
        setCampaigns(camps.items.map((c: ApiCampaign) => apiToCampaign(c, dels?.items || [])));
      })
      .catch(() => {/* keep mock fallback */})
      .finally(() => setLoading(false));
  useEffect(() => {
    setLoading(true);
    loadCampaigns(activeSlug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlug]);

  // Deep-link: /agents/campaigns?campaign=<id> auto-opens that campaign's detail once
  // campaigns load. Applied once (urlAppliedRef) so manual Back navigation isn't overridden.
  const [searchParams] = useSearchParams();
  const urlAppliedRef = useRef(false);
  useEffect(() => {
    if (urlAppliedRef.current || !campaigns.length) return;
    const wanted = searchParams.get("campaign");
    if (wanted && campaigns.some((c) => c.id === wanted)) {
      setSelectedId(wanted);
    }
    urlAppliedRef.current = true;
  }, [campaigns, searchParams]);

  // Auto-dismiss toast after a few seconds.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  const visibleCampaigns = campaigns.filter((c) => !archived.has(c.id));
  const campaign = visibleCampaigns.find((c) => c.id === selectedId);

  /* ── Campaign mutations ── */
  // Persist a campaign field update to the vault (entity/update, type campaign).
  const postCampaignUpdate = (id: string, fields: Record<string, unknown>) =>
    fetch(`${DOCS_API_DOMAIN}/api/agents/entity/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: CAMP_CLIENT_SLUG, type: "campaign", id, fields }),
    }).catch(() => {/* optimistic */});

  // Overwrite a campaign's phases, then refetch so the accordion rebuilds.
  const savePhases = (campaignId: string, phases: PhaseDef[]) => {
    setCampaigns((prev) => prev.map((c) => (c.id === campaignId ? { ...c, rawPhases: phases } : c))); // optimistic
    fetch(`${DOCS_API_DOMAIN}/api/agents/campaign/phases`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: CAMP_CLIENT_SLUG, id: campaignId, phases }),
    })
      .then((r) => (r.ok ? loadCampaigns(activeSlug) : null))
      .catch(() => {/* optimistic */});
  };

  const updateCampaign = (id: string, patch: Partial<Campaign>) => {
    setCampaigns((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    // Persist the allowlisted flat fields. `dates` is a display string backed by
    // nested frontmatter, so it's left out (the endpoint ignores it anyway).
    const fields: Record<string, unknown> = {};
    if (patch.name !== undefined) fields.name = patch.name;
    if (patch.promise !== undefined) fields.promise = patch.promise;
    if (patch.status !== undefined) fields.status = patch.status;
    if (patch.channels !== undefined) fields.channels = patch.channels;
    if (Object.keys(fields).length) postCampaignUpdate(id, fields);
  };

  const duplicateCampaign = (id: string) => {
    const src = campaigns.find((c) => c.id === id);
    if (!src) return;
    const tempId = `${src.id}-copy-${Date.now()}`;
    // Optimistic shell for instant feedback; the real (deliverables-included) copy
    // arrives via loadCampaigns(activeSlug) once the server-side clone completes.
    const copy: Campaign = {
      ...src,
      id: tempId,
      name: `${src.name} (Copy)`,
      status: "draft",
      progress: 0,
      phases: src.phases.map((p) => ({ ...p, assets: [], total: 0, secured: 0 })),
    };
    setCampaigns((prev) => {
      const idx = prev.findIndex((c) => c.id === id);
      return [...prev.slice(0, idx + 1), copy, ...prev.slice(idx + 1)];
    });
    fetch(`${DOCS_API_DOMAIN}/api/agents/campaign/duplicate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: CAMP_CLIENT_SLUG, id }),
    })
      .then((r) => (r.ok ? loadCampaigns(activeSlug) : null)) // refetch → real clone replaces the shell
      .catch(() => {/* optimistic — shell stays on failure */});
  };

  const archiveCampaign = (id: string) => {
    setArchived((prev) => new Set(prev).add(id));
    if (selectedId === id) setSelectedId(null);
    postCampaignUpdate(id, { status: "archived" });
    setToast({
      message: "Archived",
      undo: () => { setArchived((prev) => { const next = new Set(prev); next.delete(id); return next; }); postCampaignUpdate(id, { status: "active" }); },
    });
  };

  /* ── Asset mutations (operate on the campaign that owns the asset) ── */
  const mutateAsset = (campaignId: string, assetId: string, fn: (a: Asset) => Asset) =>
    setCampaigns((prev) => prev.map((c) =>
      c.id !== campaignId ? c : {
        ...c,
        phases: c.phases.map((p) => ({ ...p, assets: p.assets.map((a) => (a.id === assetId ? fn(a) : a)) })),
      }
    ));

  // Persist a deliverable field update to the vault (optimistic; logs on failure).
  const postDeliverableUpdate = (id: string, fields: Record<string, unknown>) =>
    fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: CAMP_CLIENT_SLUG, id, fields }),
    }).catch(() => {/* optimistic — UI already updated */});

  // Bind an EXISTING KB document into this campaign (+ phase) — assigns its campaign/
  // phase frontmatter so the campaign slot holds a real document with real content.
  const bindExistingDeliverable = (campaignId: string, deliverableId: string, phaseId: number) => {
    const camp = campaigns.find((c) => c.id === campaignId);
    const phaseSlug = camp?.phases.find((p) => p.id === phaseId)?.slug || null;
    Promise.resolve(postDeliverableUpdate(deliverableId, { campaign: campaignId, ...(phaseSlug ? { phase: phaseSlug } : {}) })).then(() => loadCampaigns(activeSlug));
  };

  const updateAsset = (campaignId: string, assetId: string, patch: Partial<Asset>) => {
    mutateAsset(campaignId, assetId, (a) => ({ ...a, ...patch }));
    // Translate the edit-modal patch (display values) → raw frontmatter fields.
    const fields: Record<string, unknown> = {};
    if (patch.name !== undefined) fields.title = patch.name;
    if (patch.type !== undefined) fields.deliverableType = BADGE_TO_DTYPE[patch.type] || "doc";
    if (patch.agent !== undefined) fields.owner = NAME_TO_SLUG[patch.agent] || patch.agent.toLowerCase();
    if (patch.due !== undefined) fields.due = patch.due;
    if (patch.status !== undefined) Object.assign(fields, statusToFields(patch.status));
    if (patch.phase !== undefined) fields.phase = patch.phase || null; // move across phases (null = unphased)
    if (patch.sourceUrl !== undefined) fields.sourceUrl = patch.sourceUrl || null;
    if (Object.keys(fields).length) {
      const done = postDeliverableUpdate(assetId, fields);
      // A phase change regroups the asset under a different section — refetch so the
      // move is reflected (optimistic local state can't move it across phase buckets).
      if (patch.phase !== undefined) Promise.resolve(done).then(() => loadCampaigns(activeSlug));
    }
  };

  // Open the reviewer picker; the actual write happens on confirm.
  const setAssetForReview = (campaignId: string, assetId: string) => {
    setReviewPickFor({ campaignId, assetId });
  };
  // Confirm: route the asset to the chosen reviewer(s) + stamp who submitted it + carry the
  // optional Situation/Impact/statement. Any one assigned reviewer can approve (B2).
  const confirmSetForReview = (reviewerIds: string[]) => {
    if (!reviewPickFor || reviewerIds.length === 0) return;
    const { campaignId, assetId } = reviewPickFor;
    const fields: Record<string, unknown> = {
      reviewState: "in-review",
      reviewer: reviewerIds,
      submittedBy: getCurrentMemberId(),
    };
    const hasSir = sendSir.situation.trim() || sendSir.impact.trim() || sendSir.statement.trim();
    if (hasSir) fields.sir = { situation: sendSir.situation.trim(), impact: sendSir.impact.trim(), statement: sendSir.statement.trim() };
    mutateAsset(campaignId, assetId, (a) => ({ ...a, reviewState: "in-review", status: "pending-review" }));
    postDeliverableUpdate(assetId, fields);
    setReviewPickFor(null);
    setSendSir({ situation: "", impact: "", statement: "" });
  };

  const addDeliverable = (campaignId: string, phaseId: number, seed: SeedAsset) => {
    const tempId = newAssetId();
    // Resolve the frontmatter phase slug from the numeric phase id so the new
    // deliverable lands in the right phase (not just the unphased bucket).
    const phaseSlug = campaigns.find((c) => c.id === campaignId)?.phases.find((p) => p.id === phaseId)?.slug;
    setCampaigns((prev) => prev.map((c) =>
      c.id !== campaignId ? c : {
        ...c,
        phases: c.phases.map((p) => (p.id !== phaseId ? p : { ...p, total: p.total + 1, assets: [...p.assets, { ...seed, id: tempId }] })),
      }
    ));
    // Persist a new deliverable (campaign-associated; phase is assigned via edit).
    fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/create`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        client: CAMP_CLIENT_SLUG,
        fields: {
          title: seed.name,
          deliverableType: BADGE_TO_DTYPE[seed.type] || "doc",
          campaign: campaignId,
          ...(phaseSlug ? { phase: phaseSlug } : {}),
          owner: NAME_TO_SLUG[seed.agent] || seed.agent.toLowerCase(),
          due: seed.due,
          status: "drafted",
        },
      }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        // Swap the temp id for the real vault id so later edits persist.
        if (d?.id) setCampaigns((prev) => prev.map((c) =>
          c.id !== campaignId ? c : { ...c, phases: c.phases.map((p) => ({ ...p, assets: p.assets.map((a) => (a.id === tempId ? { ...a, id: d.id } : a)) })) }
        ));
      })
      .catch(() => {/* optimistic — stays local on failure */});
  };

  const archiveAsset = (campaignId: string, assetId: string) => {
    // capture for undo
    let snapshot: { phaseId: number; index: number; asset: Asset } | null = null;
    setCampaigns((prev) => prev.map((c) => {
      if (c.id !== campaignId) return c;
      return {
        ...c,
        phases: c.phases.map((p) => {
          const index = p.assets.findIndex((a) => a.id === assetId);
          if (index === -1) return p;
          snapshot = { phaseId: p.id, index, asset: p.assets[index] };
          const wasSecured = p.assets[index].status === "secured";
          return { ...p, total: Math.max(0, p.total - 1), secured: wasSecured ? Math.max(0, p.secured - 1) : p.secured, assets: p.assets.filter((a) => a.id !== assetId) };
        }),
      };
    }));
    postDeliverableUpdate(assetId, { status: "archived" }); // soft-delete per the contract
    setToast({
      message: "Archived",
      undo: () => {
        if (!snapshot) return;
        const snap = snapshot;
        postDeliverableUpdate(assetId, { status: "drafted" }); // restore
        setCampaigns((prev) => prev.map((c) => {
          if (c.id !== campaignId) return c;
          return {
            ...c,
            phases: c.phases.map((p) => {
              if (p.id !== snap.phaseId) return p;
              const assets = [...p.assets];
              assets.splice(Math.min(snap.index, assets.length), 0, snap.asset);
              const wasSecured = snap.asset.status === "secured";
              return { ...p, total: p.total + 1, secured: wasSecured ? p.secured + 1 : p.secured, assets };
            }),
          };
        }));
      },
    });
  };

  /** Move an asset from one campaign into another's first phase (assign unfiled deliverable). */
  if (loading) {
    // UX-C1: skeleton placeholder mirroring the campaign-card grid while data is
    // fetched, so the screen doesn't flash blank before the cards arrive.
    return (
      <AppShellV8 activeApp="campaigns" breadcrumb={["Campaigns"]} reviewCount={7}>
        <div className="mx-auto max-w-6xl px-6 py-8" aria-busy="true" aria-label="Loading campaigns">
          <div className="mb-8 h-7 w-44 animate-pulse rounded-lg bg-gray-100" />
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-xl border border-black/[0.06] bg-white p-5 shadow-sm ring-1 ring-black/[0.04]">
                <div className="flex items-start gap-4">
                  <div className="h-14 w-14 shrink-0 animate-pulse rounded-full bg-gray-100" />
                  <div className="flex-1 space-y-2.5">
                    <div className="h-3.5 w-2/3 animate-pulse rounded bg-gray-100" />
                    <div className="h-3 w-1/3 animate-pulse rounded bg-gray-100" />
                    <div className="h-3 w-full animate-pulse rounded bg-gray-100" />
                  </div>
                </div>
                <div className="mt-4 flex gap-1.5">
                  <div className="h-4 w-12 animate-pulse rounded bg-gray-100" />
                  <div className="h-4 w-12 animate-pulse rounded bg-gray-100" />
                </div>
              </div>
            ))}
          </div>
          <span className="sr-only">Loading…</span>
        </div>
      </AppShellV8>
    );
  }

  const handleNewCampaign = () => {
    const name = window.prompt("New campaign name");
    if (!name || !name.trim()) return;
    fetch(`${DOCS_API_DOMAIN}/api/agents/campaign/create`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: CAMP_CLIENT_SLUG, fields: { name: name.trim() } }),
    })
      .then((r) => (r.ok ? r.json() : r.json().then((e) => { window.alert(e.error || "Couldn't create campaign"); return null; })))
      .then((d) => { if (d?.id) { loadCampaigns(activeSlug); setSelectedId(d.id); } })
      .catch(() => {});
  };

  return (
    <AppShellV8
      activeApp="campaigns"
      breadcrumb={campaign ? ["Campaigns", campaign.name] : ["Campaigns"]}
      reviewCount={7}
      headerActions={
        campaign ? (
          // Campaign-wide actions live in the top bar (right side), per Kyle.
          <OverflowMenu
            alwaysVisible
            label="Campaign actions"
            items={[
              { label: "Edit campaign", onClick: () => setEditingCampaign(true) },
              { label: "Duplicate", onClick: () => duplicateCampaign(campaign.id) },
              { label: "Archive", onClick: () => archiveCampaign(campaign.id), destructive: true },
            ]}
          />
        ) : (
          <Button
            label="+ New Campaign"
            type={ButtonTypes.OUTLINED}
            onClick={handleNewCampaign}
          />
        )
      }
    >
      {campaign ? (
        <CampaignDetail
          campaign={campaign}
          onBack={() => setSelectedId(null)}
          onEditCampaign={(patch) => updateCampaign(campaign.id, patch)}
          onSavePhases={(phases) => savePhases(campaign.id, phases)}
          onAddDeliverable={(phaseId, seed) => addDeliverable(campaign.id, phaseId, seed)}
          onBindExisting={(deliverableId, phaseId) => bindExistingDeliverable(campaign.id, deliverableId, phaseId)}
          onEditAsset={(assetId, patch) => updateAsset(campaign.id, assetId, patch)}
          onSetAssetForReview={(assetId) => setAssetForReview(campaign.id, assetId)}
          onArchiveAsset={(assetId) => archiveAsset(campaign.id, assetId)}
        />
      ) : (
        <CampaignList
          campaigns={visibleCampaigns}
          onSelect={setSelectedId}
          onNew={handleNewCampaign}
          onEditCampaign={updateCampaign}
          onDuplicateCampaign={duplicateCampaign}
          onArchiveCampaign={archiveCampaign}
        />
      )}
      {reviewPickFor && (
        <EntityPicker
          title="Send for review — pick reviewer(s)"
          endpoint="/members"
          multiple
          mapItem={(m) => ({ id: m.id, label: m.name, sublabel: m.login ? undefined : "contact" })}
          confirmLabel="Send for review"
          onConfirm={(ids) => confirmSetForReview(ids)}
          onClose={() => { setReviewPickFor(null); setSendSir({ situation: "", impact: "", statement: "" }); }}
          extraTop={
            <div className="space-y-2">
              <p className="text-[11px] font-medium text-gray-400">Add context for the reviewer (optional)</p>
              <input
                value={sendSir.situation}
                onChange={(e) => setSendSir((s) => ({ ...s, situation: e.target.value }))}
                placeholder="Situation — what's the background?"
                className="w-full rounded-lg border border-black/[0.06] px-3 py-1.5 text-[13px] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <input
                value={sendSir.impact}
                onChange={(e) => setSendSir((s) => ({ ...s, impact: e.target.value }))}
                placeholder="Impact — why does it matter?"
                className="w-full rounded-lg border border-black/[0.06] px-3 py-1.5 text-[13px] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <input
                value={sendSir.statement}
                onChange={(e) => setSendSir((s) => ({ ...s, statement: e.target.value }))}
                placeholder="A note to prepend before the asset"
                className="w-full rounded-lg border border-black/[0.06] px-3 py-1.5 text-[13px] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          }
        />
      )}
      {editingCampaign && campaign && (
        <CampaignEditModal
          campaign={campaign}
          onClose={() => setEditingCampaign(false)}
          onSave={(patch) => updateCampaign(campaign.id, patch)}
        />
      )}
      {toast && (
        <Toast
          message={toast.message}
          onUndo={() => { toast.undo(); setToast(null); }}
          onDismiss={() => setToast(null)}
        />
      )}
    </AppShellV8>
  );
}
